/**
 * 原站接口的数据契约（从运行中的 fiber store 与 /lynchpin/api/* 实测得到）。
 */

/** t = 同意，f = 不同意 */
export type AnswerKey = 't' | 'f';

/** 展示用的字母形式 —— 原站用 `n[key].toUpperCase()` 得到 */
export type AnswerLetter = 'T' | 'F';

export interface QuestOption {
  key: AnswerKey;
  content: string;
  /** 需要等待 60s 才出现的隐藏选项（实测 q13 的第二个选项带此标记） */
  hide?: boolean;
}

export interface Quest {
  key: string;
  content: string;
  /** 只有 affect 为 true 的问题才进入最终结果盘面（关键公式，见 QuestDial） */
  affect: boolean;
  options: QuestOption[];
}

/**
 * 引导对话。三种形态由 type / 字段组合决定：
 *  - 纯 content + options: string[]              → 单段文本，选项是一行按钮
 *  - type === 1，content: string[]，options 为二维数组 → 逐段推进，每段一组选项
 *  - type === 2，带 due 与 contents{t,f}          → 依据 due 指向的问题作答结果二选一
 */
export interface Guide {
  type?: 1 | 2;
  content?: string | string[];
  contents?: Record<AnswerKey, string>;
  /** 该引导依赖哪个问题的答案（type 2） */
  due?: string;
  options?: Array<string | string[]>;
}

export interface Story {
  guides: Record<string, Guide>;
  questions: Quest[];
}

/** 流程编排：quest 与 guide 交替 */
export interface FlowStep {
  type: 'quest' | 'guide';
  key: string;
}

export interface MyResult {
  value: boolean;
  /** 原站渲染为 rank.toFixed(0).padStart(7, '0') */
  rank: number;
}

export interface DetailData {
  story: Story;
  flow: FlowStep[];
}

/** 玩家选择：qId -> 't' | 'f' */
export type Selection = Record<string, AnswerKey>;

export interface MetaData {
  /** 已完成的题量，驱动顶部进度条 */
  progress: number;
  /** 今日是否已抽过签 */
  pass?: boolean;
}

export interface ApiEnvelope<T> {
  code: number;
  data: T;
  msg: string;
}

export interface SubmitResult {
  selection: Selection;
  myResult: MyResult | null;
}

/** 当前登录角色（来自鹰角账号 SDK 的角色绑定） */
export interface Role {
  uid: string;
  /** 形如 "null#7393"，展示时按 '#' 切分 */
  nickName: string;
  channelMasterId?: number;
}

/** 账号（鹰角通行证） */
export interface Account {
  hgId: string;
  phone?: string | null;
  email?: string | null;
}

export interface AccountState {
  ready: boolean;
  loading: boolean;
  role: Role | null;
  account: Account | null;
  isAccount: boolean;
  channelSelectVisible: boolean;
}

/** 界面文案（原站为内联中文；此处抽出便于本地化） */
export const COPY = {
  login: '登录',
  noRole: '无角色',
  /** 原站行为：nickName 为空时回退为"博士" */
  defaultNickname: '博士',
  synchronized: 'synchronized',
  connecting: '正在连接…',
  loadFailed: '连接失败，请检查网络设置',
  retry: '重试',
  logout: '退出登录',
  switchRole: '切换角色',
  /** 共享层文案：HTTP 5xx */
  netError: '网络错误',
  /** 共享层文案：其他请求失败 */
  requestError: '请求错误',
} as const;
